import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { LinkDirective } from '../../../../_shared/directives/link.directive';
import { ICardCustom } from '../../../../_shared/models/card/card-custom.model';
import { ImageData } from '../../../../_shared/models/image/image-data.model';
import { EMPTY_LINK, ILink } from '../../../../_shared/models/link.model';
import { MediaImage } from '../../../../_shared/models/media/media-image.model';
import { withConfigFallback } from '../../../../_shared/utils/with-config-fallback';
import { PlayButtonComponent } from '../../../../button/play-button/play-button.component';
import { HeadingComponent } from '../../../../heading/heading.component';
import { IconComponent } from '../../../../icon/icon.component';
import { ImageItemComponent } from '../../../../media/media-item/image-item/image-item.component';
import { TextHtmlComponent } from '../../../../text-html/text-html.component';

@Component({
    selector: 'sirius-card-custom',
    templateUrl: './card-custom.component.html',
    styleUrls: ['./card-custom.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgTemplateOutlet,
        ImageItemComponent,
        LinkDirective,
        IconComponent,
        TextHtmlComponent,
        HeadingComponent,
        PlayButtonComponent,
    ],
    host: {
        // Keeps the card light on dark sections.
        'class': 'is-light',
        '[class.is-linked]': 'hasLink()',
        '(mouseenter)': 'onHoverChange(true)',
        '(mouseleave)': 'onHoverChange(false)',
    },
})
export class CardCustomComponent {
    readonly config = input<ICardCustom | undefined>(undefined);
    readonly imageData = input<ImageData | undefined>(undefined);

    readonly title = input('');
    readonly textHtml = input('');
    readonly image = input<MediaImage | undefined>(undefined);
    readonly contentType = input('');
    readonly link = input<ILink>(EMPTY_LINK);

    protected readonly resolvedTitle = withConfigFallback<ICardCustom, 'title', string>(
        this.config,
        'title',
        this.title
    );
    protected readonly resolvedTextHtml = withConfigFallback<ICardCustom, 'textHtml', string>(
        this.config,
        'textHtml',
        this.textHtml
    );
    protected readonly resolvedImage = withConfigFallback<ICardCustom, 'image'>(this.config, 'image', this.image);
    protected readonly resolvedContentType = withConfigFallback<ICardCustom, 'contentType', string>(
        this.config,
        'contentType',
        this.contentType
    );
    protected readonly resolvedLink = withConfigFallback<ICardCustom, 'link', ILink>(this.config, 'link', this.link);

    protected readonly hasLink = computed(() => !!this.resolvedLink().href);

    protected readonly showCta = computed(() => {
        const cta = this.resolvedLink().title?.trim();
        return this.hasLink() && !!cta && cta !== this.resolvedTitle().trim();
    });

    readonly selectEvent = output<void>();
    readonly isHovered = signal(false);

    onHoverChange(hovered: boolean): void {
        this.isHovered.set(hovered);
    }

    onClick(): void {
        this.selectEvent.emit();
    }
}
